import * as React from "react";
import { DownloadPopover } from "../DownloadMenu";
import { DownloadType } from "../../pages/admin/videos/selectedVideoCardV2/helpers";

/**
 * Controlled download control for the surfaces that own their own download
 * logic: the project downloads tab, the public project/order share pages, the
 * orders page and the shared shortlist page.
 *
 * This used to be a second, near-identical copy of the video page's
 * `DownloadBtn` — same flat 300px list, same `bg-gray-400` (which compiles to
 * nothing, so the button was INVISIBLE while a download ran), same hardcoded
 * white label and grey glyph, same opaque shadow. It is now a thin adapter over
 * the shared `DownloadPopover`, so both controls are one implementation.
 *
 * The props below are unchanged, so no call site needed editing.
 */

interface DownloadDropdownProps {
  isOpen: boolean;
  isDownloading: boolean;
  onToggle: () => void;
  onSelect: (type: DownloadType) => void;
  onClickAway: () => void;
  /** Renders an icon-only button instead of the full "Download" button */
  small?: boolean;
  /** When true, renders the button grayed out and non-interactive */
  disabled?: boolean;
  /** Tooltip label for the icon button (only applies when small=true) */
  tooltipTitle?: string;
  allowedFileTypes?: Set<string> | null;
}

export function DownloadDropdown({
  isOpen,
  isDownloading,
  onToggle,
  onSelect,
  onClickAway,
  small = false,
  disabled = false,
  tooltipTitle,
  allowedFileTypes = null,
}: DownloadDropdownProps) {
  let tooltipLabel = tooltipTitle ?? "Download";
  if (disabled) tooltipLabel = "Download not available";
  else if (isDownloading) tooltipLabel = "Downloading...";

  return (
    <DownloadPopover
      variant={small ? "icon" : "primary"}
      open={isOpen}
      onToggle={onToggle}
      onClose={onClickAway}
      onPick={onSelect}
      disabled={disabled || isDownloading}
      label={isDownloading ? "Downloading..." : "Download"}
      tooltip={small ? tooltipLabel : undefined}
      canDownload={!isDownloading && !disabled}
      allowedTypes={allowedFileTypes}
    />
  );
}

export default DownloadDropdown;
